<template>
  <div>
    <div class="main-container">
      <div class="section-one-container">
        <Navigation
          @sectionTwo="sectionTwo"
          @sectionThree="sectionThree"
          @scrollToTop="scrollToTop"
        />
        <div class="background-photo-container">
          <img
            :src="require('@/assets/background-photo.jpg')"
            alt="Background"
            class="background-image"
          />
          <div class="hero-content">
            <h1 class="hero-title">
              {{ t('homepage.hero.title') }}
            </h1>
            <p class="hero-subtitle">
              {{ t('homepage.hero.subtitleLine1') }}<br />{{ t('homepage.hero.subtitleLine2') }}
            </p>
            <v-btn
              id="demo-btn"
              class="primary"
              outlined
              large
              style="margin-top: 1rem"
              @click="openDemoModal"
              >{{ t('homepage.hero.bookDemo') }}</v-btn
            >
            <v-btn
              id="signup-btn"
              class="primary"
              outlined
              large
              style="margin-top: 1rem"
              @click="showSignUpModal = true"
              >{{ t('homepage.hero.signUp') }}</v-btn
            >
          </div>
          <div class="hero-arrow-down">
            <div class="arrow-down" @click="sectionTwo('section-two')"></div>
          </div>
        </div>
      </div>
      <div class="section-two-container">
        <div class="section-two-inner" id="section-two">
          <div class="scroll-to-top" v-show="showScrollArrow" @click="scrollToTop">
            <div class="arrow-up">↑</div>
          </div>
          <div class="row">
            <div class="col-md-12">
              <h3 class="section-two-title text-center">
                {{ t('homepage.sectionTwo.title') }}
              </h3>
              <v-btn
                id="section-two-demo-btn"
                class="primary"
                outlined
                large
                style="margin-top: 1rem"
                @click="openDemoModal"
              >
                {{ t('homepage.sectionTwo.bookDemo') }}
              </v-btn>
            </div>
          </div>
          <div class="row photo-row">
            <div
              class="col-xl-3 col-lg-3 col-md-6 col-sm-6 col-12"
              v-for="(item, index) in image_details"
              :key="index"
            >
              <div class="image-text">
                <img :src="item.image" :alt="item.alt" class="image" />
                <div class="text-content">
                  <h3>{{ item.title }}</h3>
                  <p>{{ item.description }}</p>
                </div>
              </div>
            </div>
          </div>
          <div class="section-separator">
            <div class="separator-line"></div>
          </div>
          <div class="row statistics-row">
            <div
              class="col-xl-3 col-lg-3 col-md-6 col-sm-6 col-12"
              v-for="(stat, index) in statistics"
              :key="index"
            >
              <div class="stat-item">
                <div class="stat-number">{{ stat.number }}</div>
                <div class="stat-label">{{ stat.label }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="section-three-container">
        <div class="section-three-inner" id="section-three">
          <div class="scroll-to-top" v-show="showScrollArrow" @click="scrollToTop">
            <div class="arrow-up">↑</div>
          </div>
          <div class="row">
            <!-- USCG Video Side -->
            <div class="col-md-4 d-flex flex-column align-items-center">
              <div class="video-container-enhanced">
                <video
                  src="https://i911-public-videos-us-east-1.s3.us-east-1.amazonaws.com/USCG_video_V5.mp4"
                  controls
                  muted
                  loop
                  playsinline
                  controlslist="nodownload noplaybackrate"
                  poster="img/thumbnail.jpg"
                  class="enhanced-video"
                >
                  Your browser does not support the video tag.
                </video>
              </div>
              <br />
              <div class="section-three-title">
                <span class="title-accent">{{ t('homepage.sectionThree.titleAccent') }}</span> {{ t('homepage.sectionThree.subtitle') }}
              </div>
              <v-btn
                id="section-three-demo-btn"
                class="primary demo-btn-3"
                outlined
                large
                style="font-weight: 800"
                @click="openDemoModal"
              >
                {{ t('homepage.sectionThree.bookDemo') }}
              </v-btn>
            </div>
            <div class="col-auto d-none d-md-flex justify-content-center">
              <div class="divider"></div>
            </div>
            <div class="col-md-7 d-flex flex-column justify-content-between">
              <h2 class="section-three-main-title">
                {{ t('homepage.sectionThree.mainTitle') }}
              </h2>
              <div class="row">
                <div class="col-md-6">
                  <ul class="mt-2 list-unstyled feature-list">
                    <li
                      v-for="(item, index) in features.section1"
                      :key="`s1-${index}`"
                      class="feature-item"
                    >
                      <div class="section-three-feature-titles">{{ item.title }}</div>
                      <div class="section-three-feature-subtitle">
                        {{ item.description }}
                      </div>
                    </li>
                  </ul>
                </div>
                <div class="col-md-6">
                  <ul class="mt-2 list-unstyled feature-list">
                    <li
                      v-for="(item, index) in features.section2"
                      :key="`s2-${index}`"
                      class="feature-item"
                    >
                      <div class="section-three-feature-titles">{{ item.title }}</div>
                      <div class="section-three-feature-subtitle">
                        {{ item.description }}
                      </div>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <iframe src="/chatbot.html" allowtransparency="true" class="chatbot" />
    </div>
    <div class="footer-container">
      <div class="row">
        <Footer @openModal="openDemoModal" />
      </div>
    </div>
    <DemoModal v-model="showDemoModal" />
    <SignUpModal v-model="showSignUpModal" />
    <!-- Zendesk Chat Widget -->
    <div id="zendesk-chat-widget"></div>
  </div>
</template>

<script>
import Vue from 'vue'
import Navigation from "./Navigation.vue";
import Footer from "./Footer.vue";
import DemoModal from "./DemoModal.vue";
import SignUpModal from "./SignUpModal.vue";

const CARD_IMAGES = [
  require("@/assets/tracking2.jpg"),
  require("@/assets/helo-save.jpg"),
  require("@/assets/maritime.jpg"),
  require("@/assets/law-enforcement.jpg"),
]

export default Vue.extend({
  name: "SitePage",
  data() {
    return {
      showDemoModal: false,
      showSignUpModal: false,
      showScrollArrow: false,
    };
  },
  computed: {
    t() {
      return (key) => this.$t(key)
    },
    image_details() {
      const cards = this.$t('homepage.cards')
      return Array.isArray(cards) ? cards.map((card, i) => ({
        image: CARD_IMAGES[i],
        alt: card.alt,
        title: card.title,
        description: card.description,
      })) : []
    },
    statistics() {
      const stats = this.$t('homepage.statistics')
      return Array.isArray(stats) ? stats : []
    },
    features() {
      const f = this.$t('homepage.features')
      if (!f || typeof f !== 'object') return { section1: [], section2: [] }
      return {
        section1: Array.isArray(f.section1) ? f.section1 : [],
        section2: Array.isArray(f.section2) ? f.section2 : [],
      }
    },
  },
  methods: {
    openDemoModal() {
      this.showDemoModal = true;
    },
    sectionTwo(elementId) {
      const element = document.getElementById(elementId);
      if (element) {
        const elementTop = element.offsetTop;
        const navHeight = 80;
        const scrollPosition = elementTop - navHeight;

        window.scrollTo({
          top: scrollPosition,
          behavior: "smooth",
        });
      }
    },
    sectionThree(elementId) {
      const element = document.querySelector(".section-three-container");
      if (element) {
        const elementTop = element.offsetTop;
        const navHeight = 80;
        const scrollPosition = elementTop - navHeight;

        window.scrollTo({
          top: scrollPosition,
          behavior: "smooth",
        });
      }
    },
    scrollToTop() {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    },
    checkScrollPosition() {
      const sectionTwo = document.getElementById("section-two");
      const sectionThree = document.getElementById("section-three");
      if (sectionTwo) {
        const sectionTop = sectionTwo.offsetTop;
        const scrollPosition = window.pageYOffset;
        this.showScrollArrow = scrollPosition >= sectionTop;
      }
      if (sectionThree) {
        const sectionTop = sectionThree.offsetTop;
        const scrollPosition = window.pageYOffset;
        this.showScrollArrow = scrollPosition >= sectionTop;
      }
    },
    loadZendeskWidget() {
      // Load Zendesk Chat Widget
      if (typeof window !== "undefined" && !window.zE) {
        const script = document.createElement("script");
        script.id = "ze-snippet";
        script.src = "https://static.zdassets.com/ekr/sdk.js?key=YOUR_ZENDESK_KEY";
        script.async = true;
        document.head.appendChild(script);

        script.onload = () => {
          if (window.zE) {
            window.zE("webWidget", "show");
          }
        };
      }
    },
  },
  mounted() {
    window.addEventListener("scroll", this.checkScrollPosition);
    this.loadZendeskWidget();
  },
  beforeUnmount() {
    window.removeEventListener("scroll", this.checkScrollPosition);
  },
  components: {
    Navigation,
    Footer,
    DemoModal,
    SignUpModal,
  },
});
</script>

<style scoped>
.main-container {
  margin: 0;
  padding: 0;
  background-color: #ffffff;
  min-height: 100vh;
  position: relative;
}

.chatbot {
  position: fixed;
  bottom: 0;
  right: 30px;
  width: 400px;
  height: 600px;
  border: none;
  z-index: 9999;
  background: transparent;
}

.section-one-container {
  height: 100vh;
  display: flex;
  flex-direction: column;
  margin: 0;
  padding: 0;
  position: relative;
  background-color: #ffffff;
}

.section-two-container {
  min-height: 50vh;
  background-color: #ffffff;
}

.section-two-inner {
  display: flex;
  flex-direction: column;
  margin: 2rem 12rem;
  padding: 0;
  padding-top: 60px;
  position: relative;
  background-color: #ffffff;
}

.section-three-container {
  min-height: 75vh;
  background: linear-gradient(
    90deg,
    #1e293b 0%,
    #1e3a8a 20%,
    #1e3a8a 40%,
    #1e3a8a 60%,
    #1e3a8a 80%,
    #1e293b 100%
  );
  position: relative;
  z-index: 1;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.section-three-container::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(
      circle at 30% 70%,
      rgba(255, 255, 255, 0.1) 0%,
      transparent 50%
    ),
    radial-gradient(circle at 70% 30%, rgba(59, 130, 246, 0.2) 0%, transparent 50%);
  animation: subtlePulse 8s ease-in-out infinite;
}

@keyframes subtlePulse {
  0%,
  100% {
    opacity: 0.8;
  }
  50% {
    opacity: 1;
  }
}

.background-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  position: absolute;
  top: 0;
  left: 0;
}

.hero-content {
  position: absolute;
  top: 45%;
  right: 2.5rem;
  transform: translateY(-60%);
  text-align: left;
  color: white;
  z-index: 1;
  max-width: 43%;
}

.hero-title {
  font-size: 1.7rem;
  font-weight: 400;
  font-family: "Open Sans", sans-serif;
  line-height: 1.2;
  margin-bottom: 0.7rem;
  letter-spacing: 0.05rem;
}

.hero-subtitle {
  font-size: 1.1rem;
  font-weight: 400;
  font-family: "Open Sans", sans-serif;
  line-height: 1.5;
  margin-bottom: 0.7rem;
  letter-spacing: 0.02rem;
}

.section-two-title {
  font-size: 1.6rem;
  font-weight: 600;
  font-family: "Montserrat", "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI",
    Roboto, sans-serif;
  line-height: 1.5;
  margin-bottom: 2.2rem;
  color: #2d3748;
  letter-spacing: 0.05em;
  position: relative;
  text-align: center;
  text-transform: capitalize;
  font-style: italic;
  opacity: 0.9;
}

.section-two-subtitle {
  font-size: 1.1rem;
  font-weight: 300;
  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  line-height: 1.4;
  margin-bottom: 2rem;
  color: #1f2937;
  letter-spacing: 0.01em;
  max-width: 550px;
  margin-left: auto;
  margin-right: auto;
  opacity: 1;
}

#demo-btn {
  font-weight: 600;
  font-family: "Open Sans", sans-serif;
  border-radius: 2rem;
  font-size: 0.95rem;
  letter-spacing: 0.08rem;
  color: white;
  border-width: 2px;
  border-color: white !important;
  background-color: rgb(209, 4, 4) !important;
}

#signup-btn {
  font-weight: 600;
  font-family: "Open Sans", sans-serif;
  border-radius: 2rem;
  font-size: 0.95rem;
  letter-spacing: 0.08rem;
  color: rgb(209, 4, 4);
  border-width: 2px;
  border-color: rgb(209, 4, 4) !important;
  background-color: white !important;
  margin-left: 2%;
}

#section-two-demo-btn {
  font-weight: 600;
  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  border-radius: 2rem;
  font-size: 1.1rem;
  letter-spacing: 0.03em;
  color: white;
  border-width: 2px;
  border-color: #1e40af !important;
  background-color: #1e40af !important;
  text-transform: none;
  padding: 0.875rem 2rem;
  transition: all 0.3s ease;
  margin: 0 auto;
  margin-bottom: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 16px rgba(30, 64, 175, 0.25);
}

#section-two-demo-btn:hover {
  background-color: #1e40af !important;
  color: white !important;
  border-color: #1e40af !important;
  transform: translateY(-3px);
  box-shadow: 0 8px 25px rgba(30, 64, 175, 0.4);
}

.section-three-title {
  text-align: center;
  font-size: 1rem;
  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  color: white;
  letter-spacing: -0.025em;
  position: relative;
  margin: 1.5rem 0;
  padding: 0 1rem;
}

.section-three-main-title {
  font-size: 2.2rem;
  font-weight: 500;
  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  background: linear-gradient(135deg, #ffffff 0%, #e0e7ff 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-align: left;
  margin-bottom: 2rem;
  margin-top: 0;
  letter-spacing: -0.02em;
  line-height: 1.2;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.title-accent {
  background: linear-gradient(45deg, #dc2626, #ef4444);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-weight: 700;
}

.video-container-enhanced {
  background: transparent;
  border-radius: 3px;
  padding: 0;
  margin: 2rem 8% 1rem 8%;
  box-shadow: none;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.enhanced-video {
  width: 100%;
  height: auto;
  border-radius: 8px;
  display: block;
}

.feature-list {
  list-style-type: none;
  padding-left: 0;
  margin: 0;
}

.feature-item {
  margin-bottom: 1.2rem;
  padding: 0.8rem 1.2rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  backdrop-filter: blur(10px);
}

.section-three-feature-titles {
  font-size: 1.1rem;
  font-weight: 600;
  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  color: white;
  letter-spacing: 0.01em;
  margin-bottom: 0.6rem;
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
}

.section-three-feature-subtitle {
  font-size: 0.9rem;
  font-weight: 400;
  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  margin-bottom: 0;
  color: rgba(255, 255, 255, 0.75);
  letter-spacing: 0.01em;
  position: relative;
  z-index: 1;
  line-height: 1.5;
}

.divider {
  width: 2px;
  height: 95%;
  background: linear-gradient(
    180deg,
    transparent 0%,
    rgba(255, 255, 255, 0.3) 20%,
    rgba(255, 255, 255, 0.6) 50%,
    rgba(255, 255, 255, 0.3) 80%,
    transparent 100%
  );
  margin-top: 5vh;
  margin-right: 5vh;
  border-radius: 1px;
  box-shadow: 0 0 10px rgba(255, 255, 255, 0.1);
}

.demo-btn-3 {
  font-weight: 600;
  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  border-radius: 50px;
  font-size: 1.1rem;
  letter-spacing: 0.03em;
  color: #1e40af;
  border-width: 2px;
  border-color: white !important;
  background: linear-gradient(45deg, #ffffff, #f8fafc) !important;
  text-transform: none;
  padding: 1rem 2.5rem;
  transition: all 0.3s ease;
  margin: 1rem auto 2rem auto;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  position: relative;
  overflow: hidden;
}

.demo-btn-3::before {
  content: "";
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(30, 64, 175, 0.1), transparent);
  transition: left 0.5s ease;
}

.demo-btn-3:hover::before {
  left: 100%;
}

.demo-btn-3:hover {
  background: linear-gradient(45deg, #ffffff, #e0e7ff) !important;
  color: #1e40af !important;
  border-color: white !important;
  transform: translateY(-5px) scale(1.05);
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.25);
}

.photo-row {
  margin-top: 0;
  margin-bottom: 1.6rem;
}

.photo-row .col-md-3 {
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding-left: 0.5rem;
  padding-right: 0.5rem;
}

.image-text {
  text-align: center;
  padding: 0.8rem;
}

.image {
  width: 300px;
  height: 300px;
  border-radius: 2.4px;
  margin-bottom: 0;
  box-shadow: 0 3.2px 9.6px rgba(0, 0, 0, 0.1);
  object-fit: cover;
  display: block;
}

.text-content {
  width: 300px;
  margin: 0 auto;
  padding: 0.76rem 0.6rem;
  border-radius: 0 0 2.4px 2.4px;
}

.image-text h3 {
  font-size: 1.1rem;
  font-weight: 500;
  color: #1e40af;
  margin-bottom: 0.6rem;
  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  text-align: left;
  letter-spacing: -0.02em;
  line-height: 1.2;
  position: relative;
  padding-left: 0;
  font-weight: 500;
}

.image-text p {
  font-size: 0.9rem;
  color: #1e293b;
  line-height: 1.5;
  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  font-weight: 400;
  margin: 0;
  padding: 0;
  text-align: left;
  letter-spacing: 0.01em;
  position: relative;
  font-weight: 500;
}

@media (max-width: 991px) {
  .image-text h3 {
    font-size: 1rem;
    margin-bottom: 0.5rem;
    text-align: center;
  }

  .image-text p {
    font-size: 0.85rem;
    text-align: center;
    line-height: 1.4;
  }
}

@media (max-width: 575px) {
  .image-text h3 {
    font-size: 0.95rem;
    margin-bottom: 0.4rem;
    text-align: center;
  }

  .image-text p {
    font-size: 0.8rem;
    text-align: center;
    line-height: 1.3;
  }

  .section-two-title {
    font-size: 1.4rem;
    margin-bottom: 1rem;
    text-align: center;
  }

  .hero-title {
    font-size: 1.3rem;
    margin-bottom: 0.5rem;
    text-align: center;
  }

  .hero-subtitle {
    font-size: 0.9rem;
    margin-bottom: 0.5rem;
    text-align: center;
  }
}

.section-separator {
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 2rem 0;
}

.separator-line {
  width: 100%;
  height: 3px;
  background: linear-gradient(90deg, transparent, #d1d5db, #9ca3af, #d1d5db, transparent);
  border-radius: 2px;
}

.statistics-row {
  margin-top: 3rem;
  margin-bottom: 4rem;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 16px;
  padding: 3rem 2rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
  position: relative;
  overflow: hidden;
}

.stat-item {
  text-align: center;
  padding: 2rem 1rem;
  transition: all 0.3s ease;
  position: relative;
}

.stat-number {
  font-size: 2rem;
  font-weight: 900;
  background: linear-gradient(135deg, #1e40af 0%, #3b82f6 50%, #6366f1 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  line-height: 1.1;
  margin-bottom: 1rem;
  text-shadow: 0 2px 4px rgba(30, 64, 175, 0.1);
  position: relative;
}

.stat-number::before {
  content: "";
  position: absolute;
  top: -10px;
  left: 50%;
  transform: translateX(-50%);
  width: 40px;
  height: 2px;
  background: linear-gradient(90deg, #1e40af, #3b82f6);
  border-radius: 1px;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.stat-label {
  font-size: 0.85rem;
  font-weight: 400;
  color: #1f2937;
  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  line-height: 1.4;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  opacity: 0.9;
  position: relative;
  font-weight: 600;
}

.hero-arrow-down {
  position: absolute;
  bottom: 15px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10;
}

.arrow-down {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: rgba(220, 38, 38, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(220, 38, 38, 0.2);
  opacity: 0.9;
  transition: all 0.3s ease;
  animation: bounceDown 2s infinite;
  cursor: pointer;
}

.arrow-down::before {
  content: "";
  width: 0;
  height: 0;
  border-left: 8px solid transparent;
  border-right: 8px solid transparent;
  border-top: 12px solid white;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3));
}

.arrow-down:hover {
  color: white;
  opacity: 1;
  transform: scale(1.2);
}

@keyframes bounceDown {
  0%,
  20%,
  50%,
  80%,
  100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-5px);
  }
  60% {
    transform: translateY(-3px);
  }
}

.scroll-to-top {
  position: fixed;
  bottom: 130px;
  right: 57px;
  width: 50px;
  height: 50px;
  background: linear-gradient(135deg, red, red);
  border-radius: 25%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 20px rgba(30, 64, 175, 0.3);
  transition: all 0.3s ease;
  z-index: 1000;
}

.chatbot {
  position: fixed;
  bottom: 0;
  right: 30px;
  width: 400px;
  height: 600px;
  border: none;
  z-index: 9999;
  background: transparent;
}

.arrow-up {
  color: white;
  font-size: 25px;
  font-weight: 800;
  line-height: 1;
}

@media (max-width: 1520px) and (min-width: 1280px) {
  .section-two-inner {
    margin: 1.6rem 9.6rem;
    padding-top: 48px;
  }

  .section-two-container {
    min-height: 40vh;
  }

  .section-three-container {
    min-height: 75vh;
  }

  .hero-title {
    font-size: 1.6rem;
  }

  .hero-subtitle {
    font-size: 0.88rem;
  }

  .section-two-title {
    font-size: 1.6rem;
    margin-bottom: 1.76rem;
  }

  .image {
    width: 240px;
    height: 240px;
  }

  .text-content {
    width: 240px;
  }

  .image-text h3 {
    font-size: 1.02rem;
  }

  .image-text p {
    font-size: 0.9rem;
    font-weight: 500;
  }

  .statistics-row {
    margin-top: 2.4rem;
    margin-bottom: 3.2rem;
    padding: 2.4rem 1.6rem;
  }

  .stat-item {
    padding: 1.6rem 0.8rem;
  }

  .stat-number {
    font-size: 1.6rem;
  }

  .stat-label {
    font-size: 0.78rem;
    font-weight: 700;
  }

  .section-three-main-title {
    font-size: 1.76rem;
    margin-bottom: 1.6rem;
  }

  .section-three-feature-titles {
    font-size: 1rem;
    margin-bottom: 0.48rem;
  }

  .section-three-feature-subtitle {
    font-size: 0.75rem;
  }

  .feature-item {
    margin-bottom: 0.96rem;
    padding: 0.64rem 0.96rem;
  }

  .demo-btn-3 {
    padding: 0.8rem 2rem;
    font-size: 0.88rem;
  }

  .section-three-title {
    font-size: 0.8rem;
    margin: 1.2rem 0;
  }

  .photo-row {
    margin-bottom: 1.28rem;
  }

  .section-separator {
    margin: 1.6rem 0;
  }
}

.photo-row .col-xl-3,
.photo-row .col-lg-3,
.photo-row .col-md-6,
.photo-row .col-sm-6,
.photo-row .col-12 {
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 0.5rem;
  margin-bottom: 1rem;
}

.statistics-row .col-xl-3,
.statistics-row .col-lg-3,
.statistics-row .col-md-6,
.statistics-row .col-sm-6,
.statistics-row .col-12 {
  margin-bottom: 1rem;
}

@media (max-width: 1199px) {
  .photo-row .col-lg-3,
  .statistics-row .col-lg-3 {
    flex: 0 0 50%;
    max-width: 50%;
  }
}

@media (max-width: 991px) {
  .photo-row .col-md-6,
  .statistics-row .col-md-6 {
    flex: 0 0 50%;
    max-width: 50%;
  }

  .image {
    width: 100%;
    max-width: 220px;
    height: 220px;
  }

  .text-content {
    width: 100%;
    max-width: 220px;
  }

  .statistics-row {
    padding: 2rem 1rem;
  }

  .stat-item {
    padding: 1.5rem 0.5rem;
  }

  .stat-number {
    font-size: 1.6rem;
  }
}

@media (max-width: 575px) {
  .photo-row .col-sm-6,
  .statistics-row .col-sm-6 {
    flex: 0 0 100%;
    max-width: 100%;
  }

  .section-two-inner {
    margin: 0.5rem;
    padding-top: 40px;
  }

  .hero-content {
    top: 50%;
    right: 1rem;
    left: 1rem;
    transform: translateY(-50%);
    max-width: 100%;
    text-align: center;
  }

  .hero-title {
    font-size: 1.3rem;
    margin-bottom: 0.5rem;
  }

  .hero-subtitle {
    font-size: 0.9rem;
    margin-bottom: 0.5rem;
  }

  .section-two-title {
    font-size: 1.4rem;
    margin-bottom: 1rem;
  }

  .image {
    width: 100%;
    max-width: 200px;
    height: 200px;
  }

  .text-content {
    width: 100%;
    max-width: 200px;
  }

  .statistics-row {
    padding: 1.5rem 0.5rem;
    margin-top: 1.5rem;
    margin-bottom: 1.5rem;
  }

  .stat-item {
    padding: 1rem 0.5rem;
  }

  .stat-number {
    font-size: 1.4rem;
  }

  .scroll-to-top {
    bottom: 200px;
    right: 60px;
    width: 40px;
    height: 40px;
  }

  .arrow-up {
    font-size: 20px;
  }

  #demo-btn,
  #section-two-demo-btn {
    font-size: 0.85rem;
    padding: 0.6rem 1.2rem;
  }

  .photo-row .col-12 {
    margin-bottom: 1rem;
  }

  .image-text {
    padding: 0.5rem;
  }

  .video-container-enhanced {
    margin: 1rem 5% 1rem 5%;
    padding: 0.5rem;
  }

  .feature-item {
    padding: 0.75rem;
    margin-bottom: 1rem;
  }

  .demo-btn-3 {
    padding: 0.8rem 2rem;
    font-size: 1rem;
  }

  .section-three-title {
    font-size: 0.9rem;
    margin: 1rem 0;
  }
}

@media (min-width: 4000px) {
  .section-two-inner {
    margin: 4rem 25rem;
  }

  .hero-content {
    right: 10rem;
    max-width: 30%;
  }

  .hero-title {
    font-size: 3.2rem;
  }

  .hero-subtitle {
    font-size: 2rem;
  }

  .section-two-title {
    font-size: 3.2rem;
  }

  .image {
    height: 400px;
  }

  .statistics-row {
    padding: 5rem 4rem;
  }

  .stat-item {
    padding: 4rem 2rem;
  }

  .stat-number {
    font-size: 3rem;
  }

  .scroll-to-top {
    bottom: 200px;
    right: 60px;
    width: 70px;
    height: 70px;
  }

  .arrow-up {
    font-size: 40px;
  }
}

.footer-container {
  position: relative;
  background-color: transparent;
  width: 100%;
  margin-top: 0;
  z-index: 10;
  clear: both;
}

@media (max-width: 1024px) {
  .section-three-container {
    min-height: 80vh;
    padding: 2rem 1rem;
  }

  .section-three-inner .row {
    flex-direction: column;
    align-items: center;
  }

  .section-three-inner .col-md-4 {
    width: 100%;
    max-width: 500px;
    margin-bottom: 2rem;
  }

  .section-three-inner .col-md-7 {
    width: 100%;
    max-width: 800px;
  }

  .section-three-inner .col-auto {
    display: none !important;
  }

  .video-container-enhanced {
    margin: 0 auto;
    max-width: 100%;
    text-align: center;
  }

  .enhanced-video {
    max-height: 350px;
    width: 100%;
  }

  .section-three-title {
    text-align: center;
    font-size: 1.2rem;
    margin-bottom: 1.5rem;
  }

  .section-three-main-title {
    text-align: center;
    font-size: 2.2rem;
    margin-bottom: 2rem;
  }

  .feature-list {
    gap: 1.2rem;
  }

  .feature-item {
    padding: 1.2rem 1.2rem;
    margin-bottom: 1.2rem;
  }

  .section-three-feature-titles {
    font-size: 1.2rem;
  }

  .section-three-feature-subtitle {
    font-size: 1rem;
  }

  .demo-btn-3 {
    padding: 1rem 2rem;
    font-size: 1rem;
    display: block;
    margin: 0 auto;
  }
}

@media (max-width: 768px) {
  .section-three-container {
    min-height: 70vh;
    padding: 1.5rem 1rem;
  }

  .video-container-enhanced {
    margin: 1rem 5%;
  }

  .enhanced-video {
    width: 100%;
    height: auto;
    max-height: 300px;
  }

  .section-three-main-title {
    font-size: 1.8rem;
    margin-bottom: 1.5rem;
  }

  .section-three-title {
    font-size: 1rem;
    margin-bottom: 1.5rem;
  }

  .feature-list {
    flex-direction: column;
    gap: 1rem;
  }

  .feature-item {
    margin-bottom: 1rem;
    padding: 0.8rem 1rem;
  }

  .section-three-feature-titles {
    font-size: 1rem;
  }

  .section-three-feature-subtitle {
    font-size: 0.9rem;
  }

  .demo-btn-3 {
    padding: 0.8rem 1.5rem;
    font-size: 0.9rem;
  }
}

@media (max-width: 480px) {
  .scroll-to-top {
    bottom: 130px;
    right: 53px;
    width: 40px;
    height: 40px;
  }

  .chatbot {
    right: 20px;
  }

  .arrow-up {
    font-size: 18px;
  }

  .section-three-container {
    min-height: 65vh;
    padding: 1rem 0.8rem;
  }

  .video-container-enhanced {
    margin: 0.8rem 3%;
  }

  .enhanced-video {
    max-height: 250px;
  }

  .section-three-main-title {
    font-size: 1.6rem;
    margin-bottom: 1rem;
  }

  .section-three-title {
    font-size: 0.9rem;
    margin-bottom: 1rem;
  }

  .feature-item {
    padding: 0.6rem 0.8rem;
  }

  .section-three-feature-titles {
    font-size: 0.9rem;
  }

  .section-three-feature-subtitle {
    font-size: 0.8rem;
  }

  .demo-btn-3 {
    padding: 0.7rem 1.2rem;
    font-size: 0.8rem;
  }
}
</style>
